import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useApiFetch, useApiMutation } from '../../hooks/useApiFetch.js'
import { useSSEGenerate } from '../../hooks/useSSEGenerate.js'
import { useUIStore } from '../../stores/uiStore.js'
import { Button, Select, Textarea, Input, Spinner, Alert, Table, Modal, Badge, Pagination, FileUpload, Tabs, Drawer, ProgressBar, type TableColumn, type TabItem } from '../../components/ui/index.js'
import { formatDate } from '../../lib/format.js'
import { cn } from '../../lib/cn.js'
import styles from './AdminAITestGen.module.css'
import type { AdminSSEEvent, AdminTestGenResult, AdminTestGenHistoryItem } from '@cugumi/shared/schemas'

// ── Types ──

interface PromptTemplate {
  id: string
  name: string
  prompt: string
  style: string | null
  modelId: string | null
  role: string | null
  sampleImageUrl: string | null
  createdAt: string
}

interface TemplatesResponse {
  templates: PromptTemplate[]
}

interface AIModelInfo {
  id: string
  name: string
  provider: string
  enabled: boolean
  isDefault: boolean
  isRecommended: boolean
  costPerImage: number
  facePreservation: string
  speed: string
}

interface ModelsResponse {
  imageModels: AIModelInfo[]
  textModels: unknown[]
}

interface TestGenHistory extends AdminTestGenHistoryItem {
  cost: number | null
  creditsUsed: number | null
  isAdminTest: boolean
  originalUrl: string | null
  customPrompt: string | null
  userEmail: string
  userName: string | null
}

interface TestHistoryResponse {
  generations: TestGenHistory[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  stats: {
    totalCount: number
    totalCost: number
    avgCost: number
    failRate: number
  }
}

interface PromptBlock {
  id: string
  blockType: string
  slug: string
  nameKo: string
  nameEn: string
  promptText: string
  isBuiltin: boolean
  isActive: boolean
  sortOrder: number
}

interface BlocksResponse {
  blocks: PromptBlock[]
}

// ── Constants ──

const PROMPT_MODE_TABS: TabItem[] = [
  { key: 'blocks', label: '블록 조합' },
  { key: 'custom', label: '커스텀 프롬프트' },
]

const FALLBACK_MODEL_OPTIONS = [
  { value: 'gemini-flash-image', label: 'Gemini Flash' },
]

const ROLE_OPTIONS = [
  { value: 'GROOM', label: '신랑' },
  { value: 'BRIDE', label: '신부' },
  { value: 'COUPLE', label: '커플' },
]

const COUNT_OPTIONS = [
  { value: '1', label: '1장' },
  { value: '5', label: '5장' },
  { value: '10', label: '10장' },
]

const HIST_PAGE_SIZE = 10

const STATUS_BADGE: Record<string, { color: 'green' | 'red' | 'yellow' | 'blue' | 'gray'; label: string }> = {
  COMPLETED: { color: 'green', label: '완료' },
  FAILED: { color: 'red', label: '실패' },
  PROCESSING: { color: 'yellow', label: '처리중' },
  PENDING: { color: 'blue', label: '대기' },
}

const ROLE_LABEL: Record<string, string> = {
  GROOM: '신랑',
  BRIDE: '신부',
  COUPLE: '커플',
}

// ── Component ──

export function AdminAITestGen() {
  const { addToast } = useUIStore()

  // 설정 상태
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  // 커플 모드: 신랑(index 0), 신부(index 1) 분리 슬롯
  const [coupleFiles, setCoupleFiles] = useState<[File | null, File | null]>([null, null])
  const [couplePreviews, setCouplePreviews] = useState<[string | null, string | null]>([null, null])
  const [modelId, setModelId] = useState('')
  const [role, setRole] = useState('GROOM')
  const [count, setCount] = useState(1)
  const [customPrompt, setCustomPrompt] = useState('')

  // 프롬프트 모드: blocks vs custom
  const [promptMode, setPromptMode] = useState('blocks')
  const [styleSlug, setStyleSlug] = useState('')
  const [poseSlug, setPoseSlug] = useState('')
  const [wardrobeSlug, setWardrobeSlug] = useState('')
  const [cameraSlug, setCameraSlug] = useState('')
  const [previewPrompt, setPreviewPrompt] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)

  // SSE 스트리밍 생성
  const sse = useSSEGenerate<AdminSSEEvent>()
  const [result, setResult] = useState<AdminTestGenResult | null>(null)

  // SSE 이벤트에서 상태 파생
  const generating = sse.phase === 'generating'
  const genError = sse.error

  const statusMessage = useMemo(() => {
    const last = [...sse.events].reverse().find((e): e is Extract<AdminSSEEvent, { type: 'status' }> => e.type === 'status')
    return last?.message ?? null
  }, [sse.events])

  const streamedImages = useMemo(() => {
    return sse.events
      .filter((e): e is Extract<AdminSSEEvent, { type: 'image' }> => e.type === 'image')
      .map((e) => e.url)
  }, [sse.events])

  const batchProgress = useMemo(() => {
    const imageEvents = sse.events.filter(
      (e): e is Extract<AdminSSEEvent, { type: 'image' }> => e.type === 'image',
    )
    if (imageEvents.length === 0) return null
    const last = imageEvents[imageEvents.length - 1]
    return { completed: last.progress, total: last.total }
  }, [sse.events])

  // done 이벤트 → result 세팅 + 이력 새로고침
  const prevDoneRef = useRef<string | null>(null)

  // 템플릿 저장 모달
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [templateName, setTemplateName] = useState('')

  // 이력 상태
  const [histPage, setHistPage] = useState(1)
  const [histModelFilter, setHistModelFilter] = useState('')
  const [selectedHistItem, setSelectedHistItem] = useState<TestGenHistory | null>(null)

  // 이미지 프리뷰 모달
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

  // exit 애니메이션 중 내용 유지를 위한 ref
  const lastHistItemRef = useRef<TestGenHistory | null>(null)
  if (selectedHistItem) lastHistItemRef.current = selectedHistItem
  const displayedHistItem = selectedHistItem ?? lastHistItemRef.current

  // 모델 목록
  const { data: modelsData } = useApiFetch<ModelsResponse>('/api/admin/ai-models')

  const modelOptions = useMemo(() => {
    if (!modelsData?.imageModels?.length) return FALLBACK_MODEL_OPTIONS
    return modelsData.imageModels
      .filter((m) => m.enabled)
      .map((m) => ({
        value: m.id,
        label: `${m.name}${m.isDefault ? ' (기본)' : ''}${m.isRecommended ? ' ★' : ''}`,
      }))
  }, [modelsData])

  const estimatedCost = useMemo(() => {
    if (!modelsData?.imageModels?.length || !modelId) return null
    const model = modelsData.imageModels.find((m) => m.id === modelId)
    if (!model) return null
    return model.costPerImage * count
  }, [modelsData, modelId, count])

  // 모델 데이터 로드 시 기본 모델 설정
  useEffect(() => {
    if (!modelsData?.imageModels?.length) return
    if (modelId) return // 이미 선택된 경우 유지
    const defaultModel = modelsData.imageModels.find((m) => m.isDefault && m.enabled)
    const firstEnabled = modelsData.imageModels.find((m) => m.enabled)
    setModelId(defaultModel?.id ?? firstEnabled?.id ?? 'gemini-flash-image')
  }, [modelsData, modelId])

  // 프롬프트 블록 목록
  const { data: blocksData } = useApiFetch<BlocksResponse>('/api/admin/prompt-blocks')

  const blockOptions = useMemo(() => {
    if (!blocksData?.blocks) return { STYLE: [], POSE: [], WARDROBE: [], CAMERA: [] }
    const opts: Record<string, { value: string; label: string }[]> = {
      STYLE: [], POSE: [], WARDROBE: [], CAMERA: [],
    }
    for (const b of blocksData.blocks) {
      if (opts[b.blockType]) {
        opts[b.blockType].push({ value: b.slug, label: b.nameKo })
      }
    }
    return opts
  }, [blocksData])

  // 블록 데이터 로드 시 기본값 설정
  useEffect(() => {
    if (!blocksData?.blocks?.length) return
    if (!styleSlug) {
      const firstStyle = blocksData.blocks.find((b) => b.blockType === 'STYLE')
      if (firstStyle) setStyleSlug(firstStyle.slug)
    }
    if (!poseSlug) {
      const firstPose = blocksData.blocks.find((b) => b.blockType === 'POSE')
      if (firstPose) setPoseSlug(firstPose.slug)
    }
    if (!wardrobeSlug) {
      const firstWardrobe = blocksData.blocks.find((b) => b.blockType === 'WARDROBE')
      if (firstWardrobe) setWardrobeSlug(firstWardrobe.slug)
    }
    if (!cameraSlug) {
      const firstCamera = blocksData.blocks.find((b) => b.blockType === 'CAMERA')
      if (firstCamera) setCameraSlug(firstCamera.slug)
    }
  }, [blocksData, styleSlug, poseSlug, wardrobeSlug, cameraSlug])

  // 프롬프트 미리보기
  const handlePreview = useCallback(async () => {
    if (!styleSlug || !poseSlug || !wardrobeSlug || !cameraSlug) return
    setPreviewLoading(true)
    try {
      const res = await fetch('/api/admin/prompt-blocks/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role,
          promptBlocks: { styleSlug, poseSlug, wardrobeSlug, cameraSlug },
        }),
      })
      const json = await res.json()
      if (json.success) {
        setPreviewPrompt(json.data.prompt)
      }
    } catch {
      // ignore
    } finally {
      setPreviewLoading(false)
    }
  }, [role, styleSlug, poseSlug, wardrobeSlug, cameraSlug])

  // 템플릿 목록
  const { data: templateData, refetch: refetchTemplates } = useApiFetch<TemplatesResponse>(
    '/api/admin/ai-prompt-templates'
  )

  // 이력 목록
  const historyUrl = `/api/admin/ai-generations?isAdminTest=true&page=${histPage}&pageSize=${HIST_PAGE_SIZE}&sortBy=createdAt&sortOrder=desc${histModelFilter ? `&modelId=${histModelFilter}` : ''}`
  const { data: historyData, refetch: refetchHistory } = useApiFetch<TestHistoryResponse>(historyUrl)

  const { trigger: saveTemplate, isLoading: saving } = useApiMutation<unknown, Record<string, unknown>>(
    '/api/admin/ai-prompt-templates',
    'POST',
  )

  const { trigger: deleteTemplate } = useApiMutation<unknown, never>(
    '/api/admin/ai-prompt-templates',
    'DELETE',
  )

  // 이미지 선택 핸들러 (단일 — GROOM/BRIDE)
  const handleImageChange = useCallback((files: File[], previews: string[]) => {
    setImageFiles(files)
    setImagePreviews(previews)
  }, [])

  // 커플 이미지 선택 핸들러 (신랑=0, 신부=1)
  const handleCoupleGroomChange = useCallback((files: File[], previews: string[]) => {
    setCoupleFiles((prev) => [files[0] ?? null, prev[1]])
    setCouplePreviews((prev) => [previews[0] ?? null, prev[1]])
  }, [])

  const handleCoupleBrideChange = useCallback((files: File[], previews: string[]) => {
    setCoupleFiles((prev) => [prev[0], files[0] ?? null])
    setCouplePreviews((prev) => [prev[0], previews[0] ?? null])
  }, [])

  // 모델 필터 변경 시 페이지 리셋
  useEffect(() => {
    setHistPage(1)
  }, [histModelFilter])

  // 유효한 파일/프리뷰 (역할에 따라 분기)
  const effectiveFiles = role === 'COUPLE'
    ? coupleFiles.filter((f): f is File => f !== null)
    : imageFiles
  const effectivePreviews = role === 'COUPLE'
    ? couplePreviews.filter((p): p is string => p !== null)
    : imagePreviews

  // done 이벤트 감지 → result 세팅 + 이력 새로고침
  useEffect(() => {
    const doneEvent = sse.events.find(
      (e): e is Extract<AdminSSEEvent, { type: 'done' }> => e.type === 'done',
    )
    if (!doneEvent) return
    // 같은 done 이벤트 중복 처리 방지
    if (prevDoneRef.current === doneEvent.generationId) return
    prevDoneRef.current = doneEvent.generationId

    setResult({
      type: 'done',
      generationId: doneEvent.generationId,
      urls: doneEvent.urls,
      cost: doneEvent.cost,
      modelId: doneEvent.modelId,
      prompt: doneEvent.prompt,
    })
    addToast({ type: 'success', message: '이미지 생성 완료!' })
    refetchHistory()
  }, [sse.events, addToast, refetchHistory])

  // 생성 요청 (SSE 스트리밍)
  const handleGenerate = useCallback(async () => {
    const files = role === 'COUPLE'
      ? coupleFiles.filter((f): f is File => f !== null)
      : imageFiles

    if (files.length === 0) {
      addToast({ type: 'error', message: '참조 이미지를 선택해주세요.' })
      return
    }
    if (role === 'COUPLE' && files.length < 2) {
      addToast({ type: 'error', message: '커플 모드는 신랑/신부 이미지를 모두 선택해주세요.' })
      return
    }

    // 모든 이미지를 base64 데이터 URL로 변환
    const imageUrls = await Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          })
      )
    )

    const body: Record<string, unknown> = {
      imageUrls,
      modelId,
      role,
      count,
    }

    if (promptMode === 'blocks' && styleSlug && poseSlug && wardrobeSlug && cameraSlug) {
      body.promptBlocks = { styleSlug, poseSlug, wardrobeSlug, cameraSlug }
    } else if (customPrompt.trim()) {
      body.customPrompt = customPrompt
    } else {
      addToast({ type: 'error', message: '블록을 선택하거나 커스텀 프롬프트를 입력해주세요.' })
      return
    }

    setResult(null)
    prevDoneRef.current = null

    sse.start({
      url: '/api/admin/ai-test-generate',
      buildBody: () => JSON.stringify(body),
    })
  }, [imageFiles, coupleFiles, modelId, role, count, promptMode, styleSlug, poseSlug, wardrobeSlug, cameraSlug, customPrompt, addToast, sse])

  // 템플릿 저장
  const handleSaveTemplate = useCallback(async () => {
    if (!templateName.trim() || !customPrompt.trim()) return

    const res = await saveTemplate({
      name: templateName,
      prompt: customPrompt || previewPrompt,
      modelId: modelId || undefined,
      role: role || undefined,
      sampleImageUrl: result?.urls[0] || undefined,
    })

    if (res) {
      addToast({ type: 'success', message: '템플릿이 저장되었습니다.' })
      setShowSaveModal(false)
      setTemplateName('')
      refetchTemplates()
    } else {
      addToast({ type: 'error', message: '템플릿 저장 실패' })
    }
  }, [templateName, customPrompt, previewPrompt, modelId, role, result, saveTemplate, addToast, refetchTemplates])

  // 템플릿 삭제
  const handleDeleteTemplate = useCallback(async (id: string) => {
    const res = await deleteTemplate(undefined, { pathOverride: `/api/admin/ai-prompt-templates/${id}` })
    if (res !== undefined) {
      addToast({ type: 'success', message: '템플릿이 삭제되었습니다.' })
      refetchTemplates()
    }
  }, [deleteTemplate, addToast, refetchTemplates])

  // 템플릿 사용
  const handleUseTemplate = useCallback((tpl: PromptTemplate) => {
    setCustomPrompt(tpl.prompt)
    setPromptMode('custom')
    if (tpl.modelId) setModelId(tpl.modelId)
    if (tpl.role) setRole(tpl.role)
    addToast({ type: 'success', message: `"${tpl.name}" 템플릿 적용` })
  }, [addToast])

  const templateColumns: TableColumn<PromptTemplate>[] = [
    { key: 'name', label: '이름', width: '150px' },
    {
      key: 'prompt',
      label: '프롬프트',
      render: (row) => (
        <span className={styles.promptPreview}>{row.prompt.slice(0, 80)}{row.prompt.length > 80 ? '...' : ''}</span>
      ),
    },
    { key: 'style', label: '스타일', render: (row) => row.style ?? '-', width: '120px' },
    { key: 'modelId', label: '모델', render: (row) => row.modelId ?? '-', width: '120px' },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className={styles.templateActions}>
          <Button size="sm" variant="secondary" onClick={() => handleUseTemplate(row)}>사용</Button>
          <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(row.id)}>삭제</Button>
        </div>
      ),
      width: '140px',
    },
  ]

  const historyColumns: TableColumn<TestGenHistory>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (row) => <span className={styles.idCell}>{row.id.slice(0, 8)}</span>,
      width: '90px',
    },
    {
      key: 'createdAt',
      label: '생성일시',
      render: (row) => formatDate(row.createdAt, 'M/d HH:mm'),
      width: '100px',
    },
    {
      key: 'modelId',
      label: '모델',
      render: (row) => row.modelId ?? '-',
      width: '140px',
    },
    {
      key: 'style',
      label: '스타일',
      render: (row) => row.style ?? '-',
      width: '120px',
    },
    {
      key: 'role',
      label: '역할',
      render: (row) => row.role ? (ROLE_LABEL[row.role] ?? row.role) : '-',
      width: '70px',
    },
    {
      key: 'status',
      label: '상태',
      render: (row) => {
        const badge = STATUS_BADGE[row.status] ?? { color: 'gray' as const, label: row.status }
        return <Badge color={badge.color}>{badge.label}</Badge>
      },
      width: '80px',
    },
    {
      key: 'cost',
      label: '비용',
      render: (row) => row.cost != null ? `$${row.cost.toFixed(4)}` : '-',
      width: '80px',
    },
    {
      key: 'generatedUrls',
      label: '이미지',
      render: (row) => {
        const count = row.generatedUrls?.length ?? 0
        return count > 0 ? <Badge color="blue">{count}장</Badge> : <span>-</span>
      },
      width: '70px',
    },
  ]

  return (
    <div className={styles.root}>
      {/* 설정 영역 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>AI 이미지 생성 테스트</h3>
        <div className={styles.configGrid}>
          {role === 'COUPLE' ? (
            <div className={styles.coupleUploadRow}>
              <FileUpload
                label="신랑 이미지"
                accept="image/jpeg,image/png"
                placeholder="신랑 사진을 업로드하세요"
                files={coupleFiles[0] ? [coupleFiles[0]] : []}
                previews={couplePreviews[0] ? [couplePreviews[0]] : []}
                onChange={handleCoupleGroomChange}
              />
              <FileUpload
                label="신부 이미지"
                accept="image/jpeg,image/png"
                placeholder="신부 사진을 업로드하세요"
                files={coupleFiles[1] ? [coupleFiles[1]] : []}
                previews={couplePreviews[1] ? [couplePreviews[1]] : []}
                onChange={handleCoupleBrideChange}
              />
            </div>
          ) : (
            <FileUpload
              label="참조 이미지"
              accept="image/jpeg,image/png"
              files={imageFiles}
              previews={imagePreviews}
              onChange={handleImageChange}
            />
          )}

          <div className={styles.configRow}>
            <div className={styles.configField}>
              <label className={styles.label}>모델</label>
              <Select
                size="sm"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                options={modelOptions}
              />
            </div>
            <div className={styles.configField}>
              <label className={styles.label}>역할</label>
              <div className={styles.countToggle}>
                {ROLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(styles.countBtn, role === opt.value && styles.countBtnActive)}
                    onClick={() => setRole(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.configField}>
              <label className={styles.label}>생성 수량</label>
              <div className={styles.countToggle}>
                {COUNT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(styles.countBtn, count === Number(opt.value) && styles.countBtnActive)}
                    onClick={() => setCount(Number(opt.value))}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <Tabs items={PROMPT_MODE_TABS} value={promptMode} onChange={setPromptMode} />

          {promptMode === 'blocks' ? (
            <>
              <div className={styles.configRow}>
                <div className={styles.configField}>
                  <label className={styles.label}>스타일</label>
                  <Select
                    size="sm"
                    value={styleSlug}
                    onChange={(e) => setStyleSlug(e.target.value)}
                    options={blockOptions.STYLE.length > 0 ? blockOptions.STYLE : [{ value: '', label: '로딩 중...' }]}
                  />
                </div>
                <div className={styles.configField}>
                  <label className={styles.label}>포즈</label>
                  <Select
                    size="sm"
                    value={poseSlug}
                    onChange={(e) => setPoseSlug(e.target.value)}
                    options={blockOptions.POSE.length > 0 ? blockOptions.POSE : [{ value: '', label: '로딩 중...' }]}
                  />
                </div>
                <div className={styles.configField}>
                  <label className={styles.label}>의상</label>
                  <Select
                    size="sm"
                    value={wardrobeSlug}
                    onChange={(e) => setWardrobeSlug(e.target.value)}
                    options={blockOptions.WARDROBE.length > 0 ? blockOptions.WARDROBE : [{ value: '', label: '로딩 중...' }]}
                  />
                </div>
              </div>
              <div className={styles.configRow}>
                <div className={styles.configField}>
                  <label className={styles.label}>카메라</label>
                  <Select
                    size="sm"
                    value={cameraSlug}
                    onChange={(e) => setCameraSlug(e.target.value)}
                    options={blockOptions.CAMERA.length > 0 ? blockOptions.CAMERA : [{ value: '', label: '로딩 중...' }]}
                  />
                </div>
                <div className={styles.configField} style={{ justifyContent: 'flex-end' }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={previewLoading || !styleSlug || !poseSlug || !wardrobeSlug || !cameraSlug}
                  >
                    {previewLoading ? '로딩...' : '프롬프트 미리보기'}
                  </Button>
                </div>
              </div>
              {previewPrompt && (
                <div className={styles.configField}>
                  <label className={styles.label}>조립된 프롬프트</label>
                  <pre className={styles.promptPreviewBlock}>{previewPrompt}</pre>
                </div>
              )}
            </>
          ) : (
            <div className={styles.configField}>
              <label className={styles.label}>커스텀 프롬프트</label>
              <Textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="직접 프롬프트를 입력하세요"
                rows={4}
              />
            </div>
          )}

          <div className={styles.generateRow}>
            <Button
              onClick={handleGenerate}
              disabled={generating || effectiveFiles.length === 0 || (role === 'COUPLE' && effectiveFiles.length < 2)}
              variant="primary"
            >
              {generating ? <><Spinner size="sm" /> 생성 중...</> : count > 1 ? `${count}장 생성` : '생성'}
            </Button>
            {estimatedCost !== null && count > 1 && (
              <span className={styles.estimatedCost}>
                예상 비용: ${estimatedCost.toFixed(4)}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* 에러 */}
      {genError && <Alert variant="danger">{genError}</Alert>}

      {/* SSE 진행 상태 */}
      {generating && (
        <section className={styles.progressArea}>
          <div className={styles.statusRow}>
            <Spinner size="sm" />
            <span className={styles.statusMessage}>{statusMessage ?? '준비 중...'}</span>
          </div>
          {batchProgress && batchProgress.total > 1 && (
            <div className={styles.batchProgressRow}>
              <ProgressBar
                value={(batchProgress.completed / batchProgress.total) * 100}
                size="sm"
                animated
              />
              <span className={styles.batchProgressLabel}>
                {batchProgress.completed}/{batchProgress.total} 완료
              </span>
            </div>
          )}
          {streamedImages.length > 0 && (
            <div className={styles.streamedImages}>
              {streamedImages.map((url, i) => (
                <img key={i} src={url} alt={`생성 중 ${i + 1}`} className={styles.streamedThumb} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* 결과 뷰어 (원본 ↔ 생성 비교) */}
      {result && (
        <section className={styles.section}>
          <div className={styles.resultHeader}>
            <h3 className={styles.sectionTitle}>결과</h3>
          </div>
          <div className={styles.resultViewer}>
            <div className={styles.resultCompare}>
              <div className={styles.compareCol}>
                <label className={styles.label}>원본 이미지 ({effectivePreviews.length}장)</label>
                {effectivePreviews.length > 0 ? (
                  <div className={styles.compareGrid}>
                    {effectivePreviews.map((src, i) => (
                      <img key={i} src={src} alt={`원본 ${i + 1}`} className={styles.compareThumb} />
                    ))}
                  </div>
                ) : (
                  <div className={cn(styles.compareImage, styles.comparePlaceholder)}>원본 없음</div>
                )}
              </div>
              <div className={styles.compareCol}>
                <label className={styles.label}>생성 이미지 ({result.urls.length}장)</label>
                <div className={styles.compareGrid}>
                  {result.urls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`생성 결과 ${i + 1}`}
                      className={styles.compareThumb}
                      onClick={() => setPreviewImageUrl(url)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.resultMetaBar}>
              <Badge color="blue">{result.modelId}</Badge>
              <span className={styles.resultMetaText}>비용: ${result.cost.toFixed(4)}</span>
              {result.prompt && (
                <span className={styles.resultMetaText}>
                  프롬프트: {result.prompt.slice(0, 60)}{result.prompt.length > 60 ? '...' : ''}
                </span>
              )}
            </div>
            <div className={styles.resultActions}>
              <Button size="sm" variant="secondary" onClick={() => setShowSaveModal(true)}>
                템플릿으로 저장
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* 테스트 생성 이력 */}
      <section className={styles.section}>
        <div className={styles.histHeader}>
          <h3 className={styles.sectionTitle}>테스트 생성 이력</h3>
          <div className={styles.histFilters}>
            <Select
              size="sm"
              value={histModelFilter}
              onChange={(e) => setHistModelFilter(e.target.value)}
              options={[{ value: '', label: '전체 모델' }, ...modelOptions]}
            />
          </div>
        </div>
        {historyData?.generations.length ? (
          <>
            <Table<TestGenHistory>
              columns={historyColumns}
              data={historyData.generations}
              rowKey={(row) => row.id}
              onRowClick={(row) => setSelectedHistItem(row)}
            />
            {historyData.pagination.totalPages > 1 && (
              <Pagination
                current={histPage}
                total={historyData.pagination.totalPages}
                onChange={setHistPage}
                size="sm"
              />
            )}
          </>
        ) : (
          <p className={styles.emptyText}>테스트 생성 이력이 없습니다.</p>
        )}
      </section>

      {/* 저장된 템플릿 목록 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>저장된 프롬프트 템플릿</h3>
        <Table<PromptTemplate>
          columns={templateColumns}
          data={templateData?.templates ?? []}
          rowKey={(row) => row.id}
        />
      </section>

      {/* 상세 결과 드로어 */}
      <Drawer
        open={!!selectedHistItem}
        onClose={() => setSelectedHistItem(null)}
        title="생성 결과 상세"
        size="lg"
      >
        {displayedHistItem && (
          <div className={styles.detailContent}>
            <div className={styles.detailCompare}>
              <div className={styles.detailCol}>
                <label className={styles.label}>원본 이미지</label>
                {displayedHistItem.originalUrl ? (
                  <img src={displayedHistItem.originalUrl} alt="원본" className={styles.detailOriginal} />
                ) : (
                  <div className={cn(styles.detailOriginal, styles.comparePlaceholder)}>원본 없음</div>
                )}
              </div>
              <div className={styles.detailCol}>
                <label className={styles.label}>
                  생성 이미지 ({displayedHistItem.generatedUrls?.length ?? 0}장)
                </label>
                <div className={styles.compareGrid}>
                  {displayedHistItem.generatedUrls?.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`생성 ${i + 1}`}
                      className={styles.detailThumb}
                      onClick={() => setPreviewImageUrl(url)}
                    />
                  ))}
                  {(!displayedHistItem.generatedUrls || displayedHistItem.generatedUrls.length === 0) && (
                    <span className={styles.emptyText}>생성된 이미지 없음</span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.detailMeta}>
              <div className={styles.detailMetaRow}>
                <span className={styles.detailMetaLabel}>모델</span>
                <Badge color="blue">{displayedHistItem.modelId ?? '-'}</Badge>
              </div>
              <div className={styles.detailMetaRow}>
                <span className={styles.detailMetaLabel}>스타일</span>
                <span>{displayedHistItem.style ?? '-'}</span>
              </div>
              <div className={styles.detailMetaRow}>
                <span className={styles.detailMetaLabel}>역할</span>
                <span>{displayedHistItem.role ? (ROLE_LABEL[displayedHistItem.role] ?? displayedHistItem.role) : '-'}</span>
              </div>
              <div className={styles.detailMetaRow}>
                <span className={styles.detailMetaLabel}>비용</span>
                <span>{displayedHistItem.cost != null ? `$${displayedHistItem.cost.toFixed(4)}` : '-'}</span>
              </div>
              <div className={styles.detailMetaRow}>
                <span className={styles.detailMetaLabel}>상태</span>
                {(() => {
                  const badge = STATUS_BADGE[displayedHistItem.status] ?? { color: 'gray' as const, label: displayedHistItem.status }
                  return <Badge color={badge.color}>{badge.label}</Badge>
                })()}
              </div>
              <div className={styles.detailMetaRow}>
                <span className={styles.detailMetaLabel}>생성일시</span>
                <span>{formatDate(displayedHistItem.createdAt, 'yyyy년 M월 d일 HH:mm:ss')}</span>
              </div>
            </div>

            {displayedHistItem.customPrompt && (
              <div className={styles.detailPromptSection}>
                <label className={styles.label}>프롬프트</label>
                <p className={styles.detailPrompt}>{displayedHistItem.customPrompt}</p>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* 이미지 프리뷰 모달 */}
      <Modal
        open={!!previewImageUrl}
        onClose={() => setPreviewImageUrl(null)}
        title="이미지 프리뷰"
        size="lg"
      >
        {previewImageUrl && (
          <div className={styles.imagePreviewModal}>
            <img src={previewImageUrl} alt="프리뷰" className={styles.imagePreviewFull} />
            <a
              href={previewImageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.imagePreviewLink}
            >
              원본 크기로 보기
            </a>
          </div>
        )}
      </Modal>

      {/* 템플릿 저장 모달 */}
      <Modal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="프롬프트 템플릿 저장"
      >
        <div className={styles.modalContent}>
          <Input
            label="템플릿 이름"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="예: 해변 드라마틱 스타일"
          />
          <div className={styles.modalPromptPreview}>
            <label className={styles.label}>프롬프트</label>
            <p className={styles.promptText}>{customPrompt.slice(0, 200)}{customPrompt.length > 200 ? '...' : ''}</p>
          </div>
          <div className={styles.modalActions}>
            <Button variant="ghost" onClick={() => setShowSaveModal(false)}>취소</Button>
            <Button
              variant="primary"
              onClick={handleSaveTemplate}
              disabled={saving || !templateName.trim()}
            >
              저장
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
